/**
 * Per-conversation composer drafts (APP-3404). ChatBox owns the composer state internally and remounts on every
 * thread switch (key={navKey}), so unsent text would be lost on navigation. This zero-DOM bridge — mounted inside
 * the composer chrome so `useChatComposer` resolves — mirrors the composer value into the controller's draft map
 * under the active thread's key on every change, and restores that conversation's draft into the empty composer on
 * each remount. Sending clears the composer (store.clearComposer, optimistic), which the mirror writes through as an
 * empty value (dropping the entry), so sent text is never resurrected.
 */

import { useEffect, useRef } from "react"
import { useChatComposer } from "@mui/x-chat-headless"
import { draftKeyForThread } from "./composer-drafts"
import { useKellyChat } from "./kelly-chat-context"

export function ComposerDraftBridge() {
  const { value, setValue } = useChatComposer()
  const { activeThreadId, readDraft, writeDraft } = useKellyChat()
  // A New chat has no thread id yet; its draft is keyed under the shared sentinel so first-message text survives a switch.
  const draftKey = draftKeyForThread(activeThreadId)

  // Restore this conversation's saved draft into the freshly-mounted (empty) composer. draftKey also changes
  // mid-mount (early adoption and its rollback), but never toward a key that holds a draft — a freshly adopted
  // id has never been written, and the key we return to was emptied by the migration below — so it no-ops there
  // instead of clobbering text the user is still typing.
  useEffect(() => {
    const draft = readDraft(draftKey)
    if (draft) setValue(draft)
  }, [draftKey, readDraft, setValue])

  // Mirror the composer value through to the draft map. Skip the mount tick so the initial empty value can't
  // wipe the draft the restore effect is about to reinstate; every real change after that writes through.
  const mountTickRef = useRef(true)
  // The key we last mirrored under. When draftKey changes WITHIN a mount (sentinel -> adopted thread id at early
  // adoption, and back again if that adoption rolls back — neither remounts ChatBox), migrate the value to the new
  // key and drop the old entry — else a fossil survives (the sentinel is prune-exempt) and later resurrects text.
  const prevDraftKeyRef = useRef(draftKey)
  useEffect(() => {
    if (mountTickRef.current) {
      mountTickRef.current = false
      prevDraftKeyRef.current = draftKey
      return
    }
    const previousKey = prevDraftKeyRef.current
    if (previousKey !== draftKey) {
      prevDraftKeyRef.current = draftKey
      writeDraft(previousKey, "")
    }
    writeDraft(draftKey, value)
  }, [draftKey, value, writeDraft])

  return null
}
